import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, CheckCircle, Play, User, DollarSign, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth, API } from '../App';
import { useCurrency } from '../components/CurrencyProvider';
import axios from 'axios';

export default function CorporateBookingsDetail() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { format: formatCurrency } = useCurrency();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const profileRes = await axios.get(`${API}/corporates/me`, { headers: { Authorization: `Bearer ${token}` } });
        const bookingsRes = await axios.get(`${API}/bookings/corporate/${profileRes.data.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setBookings(bookingsRes.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchBookings();
  }, [token]);

  const filtered = bookings.filter(b => {
    if (statusFilter !== 'all' && b.training_status !== statusFilter) return false;
    if (searchTerm && !(b.trainer_name || '').toLowerCase().includes(searchTerm.toLowerCase()) && !(b.job_title || '').toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const statusCounts = {
    all: bookings.length,
    confirmed: bookings.filter(b => b.training_status === 'confirmed').length,
    in_progress: bookings.filter(b => b.training_status === 'in_progress').length,
    completed: bookings.filter(b => b.training_status === 'completed').length,
  };

  const totalSpent = bookings.filter(b => b.training_status === 'completed').reduce((sum, b) => sum + (b.amount || 0), 0);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/corporate/dashboard')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="font-outfit text-3xl font-bold text-slate-900" data-testid="page-title">Bookings & Training</h1>
          <p className="text-slate-500 mt-1">Track all your training bookings and their progress</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'All Bookings', count: statusCounts.all, filter: 'all' },
            { label: 'Confirmed', count: statusCounts.confirmed, filter: 'confirmed' },
            { label: 'In Progress', count: statusCounts.in_progress, filter: 'in_progress' },
            { label: 'Completed', count: statusCounts.completed, filter: 'completed' },
          ].map(c => (
            <div key={c.filter} onClick={() => setStatusFilter(c.filter)}
              className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${statusFilter === c.filter ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}
              data-testid={`filter-${c.filter}`}>
              <span className="text-sm text-slate-500">{c.label}</span>
              <div className="font-outfit text-2xl font-bold text-slate-900 mt-1">{c.count}</div>
            </div>
          ))}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <span className="text-sm text-slate-500">Total Spent</span>
            <div className="font-outfit text-2xl font-bold text-emerald-600 mt-1">{formatCurrency(totalSpent)}</div>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by trainer or job title..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-bookings" />
          </div>
        </div>

        {/* Bookings Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Trainer</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Training</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Dates</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Amount</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Rating</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Booked On</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} className="p-12 text-center text-slate-500">No bookings found</td></tr>
                ) : filtered.map(b => (
                  <tr key={b.id} className="hover:bg-slate-50 transition-colors" data-testid={`booking-row-${b.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center text-blue-700 font-bold text-sm">{(b.trainer_name || '?').charAt(0)}</div>
                        <div>
                          <div className="font-medium text-slate-900">{b.trainer_name || 'Unknown'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-slate-600">{b.job_title || 'Training'}</td>
                    <td className="p-4 text-sm text-slate-600">
                      {b.start_date && <span>{new Date(b.start_date).toLocaleDateString()}</span>}
                      {b.end_date && <span> - {new Date(b.end_date).toLocaleDateString()}</span>}
                    </td>
                    <td className="p-4 text-sm font-medium text-slate-900">{formatCurrency(b.amount || 0)}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full font-medium ${
                        b.training_status === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                        b.training_status === 'in_progress' ? 'bg-blue-50 text-blue-700' :
                        b.training_status === 'confirmed' ? 'bg-amber-50 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {b.training_status === 'completed' && <CheckCircle className="w-3 h-3" />}
                        {b.training_status === 'in_progress' && <Play className="w-3 h-3" />}
                        {b.training_status === 'confirmed' && <Clock className="w-3 h-3" />}
                        {b.training_status}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-slate-600">{b.feedback ? `${b.feedback.overall_rating}/5` : '-'}</td>
                    <td className="p-4 text-sm text-slate-500">{new Date(b.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-slate-50 border-t text-xs text-slate-500">Showing {filtered.length} of {bookings.length} bookings</div>
        </div>
      </div>
    </div>
  );
}
